import { useCallback, useEffect, useRef, useState } from "react";
import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { cn } from "../../lib/utils.ts";
import { labelColorHex } from "@/shared/routines.ts";
import { useLabelConfig, useRoutineMutations } from "../../hooks/useRoutines.ts";
import type {
  EmailLabelCatalogItem,
  EmailLabelColor,
  EmailLabelGroup,
} from "../../types/routines.ts";
import { Button } from "../ui/button.tsx";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select.tsx";
import { Toggle } from "../ui/toggle.tsx";
import { errorToast } from "../ui/toast.tsx";

/** Display swatch per semantic color token (mirrors the provider palettes).
 *  Shared with native, so a label reads the same color on either client. */
export const COLOR_HEX = labelColorHex;
const COLORS = Object.keys(COLOR_HEX) as EmailLabelColor[];

export function ColorDot({ color }: { color: EmailLabelColor }) {
  return (
    <span style={{ backgroundColor: COLOR_HEX[color] }} className="h-3 w-3 shrink-0 rounded-full" />
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: EmailLabelColor;
  onChange: (c: EmailLabelColor) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as EmailLabelColor)}>
      <SelectTrigger aria-label="Label color" className="h-7 w-auto gap-1 px-1.5">
        <ColorDot color={value} />
      </SelectTrigger>
      <SelectContent>
        {COLORS.map((c) => (
          <SelectItem key={c} value={c}>
            <div className="flex items-center gap-2">
              <ColorDot color={c} />
              <span className="capitalize">{c}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A built-in label: name + description are read-only; color + auto-archive editable. */
function BuiltInRow({
  label,
  onChange,
  // Auto Archive is a mailbox action, so it's inert while Auto Inbox is paused.
  paused = false,
}: {
  label: EmailLabelCatalogItem;
  onChange: (next: EmailLabelCatalogItem) => void;
  paused?: boolean;
}) {
  return (
    <div className={cn(cardSurfaceClass, "flex items-start gap-3")}>
      <div className={cn("mt-0.5", !label.enabled && "opacity-40")}>
        <ColorPicker value={label.color} onChange={(color) => onChange({ ...label, color })} />
      </div>
      <div className={cn("min-w-0 flex-1", !label.enabled && "opacity-40")}>
        <div className="text-sm font-medium">{label.name}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{label.description}</div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <LabeledToggle
          label="Enabled"
          checked={label.enabled}
          onChange={(enabled) => onChange({ ...label, enabled })}
        />
        <LabeledToggle
          label="Auto Archive"
          checked={label.autoArchive}
          onChange={(autoArchive) => onChange({ ...label, autoArchive })}
          dimmed={paused}
        />
      </div>
    </div>
  );
}

/** A custom label: fully editable (name, description, group, color, auto-archive) + delete. */
function CustomRow({
  label,
  onChange,
  onCommit,
  onDelete,
  // Auto Archive is a mailbox action, so it's inert while Auto Inbox is paused.
  paused = false,
}: {
  label: EmailLabelCatalogItem;
  /** Local edit (text typing), not persisted. */
  onChange: (next: EmailLabelCatalogItem) => void;
  /** Persist the change (instant controls + text-field blur). */
  onCommit: (next: EmailLabelCatalogItem) => void;
  onDelete: () => void;
  paused?: boolean;
}) {
  // Persist only once the custom label has a name (the server requires one);
  // before that, keep edits local so an unnamed draft doesn't error on blur.
  const commit = (next: EmailLabelCatalogItem) =>
    next.name.trim() ? onCommit(next) : onChange(next);
  return (
    <div className={cn(cardSurfaceClass, "flex flex-col gap-2", !label.enabled && "opacity-60")}>
      <div className="flex items-center gap-2">
        <ColorPicker value={label.color} onChange={(color) => commit({ ...label, color })} />
        <Input
          value={label.name}
          onChange={(e) => onChange({ ...label, name: e.target.value })}
          onBlur={() => commit(label)}
          placeholder="Label name"
          className="h-8 text-sm font-medium"
        />
        <LabeledToggle
          label="Enabled"
          checked={label.enabled}
          onChange={(enabled) => commit({ ...label, enabled })}
        />
        <Button
          data-statsig-name="label-catalog-delete-custom"
          variant="ghost"
          size="xs"
          onClick={onDelete}
        >
          Delete
        </Button>
      </div>
      <Input
        value={label.description}
        onChange={(e) => onChange({ ...label, description: e.target.value })}
        onBlur={() => commit(label)}
        placeholder="When should this label apply?"
        className="h-8 text-sm"
      />
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {(["type", "signal"] as EmailLabelGroup[]).map((g) => (
            <Button
              key={g}
              data-statsig-name="label-catalog-set-group"
              variant={label.group === g ? "default" : "outline"}
              size="xs"
              onClick={() => commit({ ...label, group: g })}
            >
              {g}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <LabeledToggle
            label="Auto Archive"
            checked={label.autoArchive}
            onChange={(autoArchive) => commit({ ...label, autoArchive })}
            dimmed={paused}
          />
        </div>
      </div>
    </div>
  );
}

function LabeledToggle({
  label,
  checked,
  onChange,
  // Dimmed (but still interactive) when the control has no current effect —
  // e.g. a push-only toggle while Auto Inbox is paused.
  dimmed = false,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  dimmed?: boolean;
}) {
  return (
    <div className={cn("flex shrink-0 items-center gap-2", dimmed && "opacity-50")}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <Toggle checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/** A bordered settings row: a labeled control with a description and a right-aligned toggle. */
function ToggleRow({
  label,
  description,
  checked,
  onChange,
  // Dimmed (but still interactive) when the setting has no current effect —
  // e.g. a push-only capability while Auto Inbox is paused.
  dimmed = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  dimmed?: boolean;
}) {
  return (
    <div
      className={cn(cardSurfaceClass, "flex items-center justify-between", dimmed && "opacity-50")}
    >
      <div className="text-left">
        <Label>{label}</Label>
        <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
      </div>
      <Toggle checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

type CatalogState = {
  builtIns: EmailLabelCatalogItem[];
  customLabels: EmailLabelCatalogItem[];
  labelPrefix: boolean;
  autoLabel: boolean;
};

const updateAt = <T,>(arr: T[], i: number, next: T): T[] =>
  arr.map((v, idx) => (idx === i ? next : v));

/**
 * Editor for the "Labeling" capability of the `auto-inbox` routine. The
 * `autoLabel` toggle governs whether classified labels are pushed to the mailbox
 * (classification always runs and is stored locally regardless). Built-in labels
 * show with read-only name/description (their tuned text is code-owned); the user
 * may recolor them, toggle auto-archive, and add/edit/delete their own custom
 * labels. Auto-saves the whole config to the routineConfig ent on every change
 * (text fields commit on blur).
 */
export function LabelCatalogEditor({
  systemId,
  masterEnabled,
}: {
  systemId: string;
  /** The routine's master on/off. When off, push-only controls are dimmed (still editable). */
  masterEnabled: boolean;
}) {
  const { config, isLoading } = useLabelConfig(systemId);
  const { saveLabelConfig } = useRoutineMutations();

  // Push-only controls (apply-to-mailbox, label prefix, per-label auto-archive)
  // have no effect while the routine is paused, so we dim them. Classification and
  // the catalog itself stay live because labels are still computed in Okthx.
  const paused = !masterEnabled;

  const [builtIns, setBuiltIns] = useState<EmailLabelCatalogItem[]>([]);
  const [customLabels, setCustomLabels] = useState<EmailLabelCatalogItem[]>([]);
  const [labelPrefix, setLabelPrefix] = useState(false);
  const [autoLabel, setAutoLabel] = useState(true);

  // Synchronous mirror of the latest state so a handler can build the full
  // catalog snapshot to auto-save without waiting for a re-render.
  const stateRef = useRef<CatalogState>({ builtIns, customLabels, labelPrefix, autoLabel });

  // Every custom label id this page has loaded or sent. okthx can add a label
  // while the page is open, and the server keeps any stored label outside this
  // set, so a save here never deletes a label the page has not shown.
  const seenCustomLabelIds = useRef(new Set<string>());

  useEffect(() => {
    if (!config) return;
    const seeded: CatalogState = {
      builtIns: config.builtIns,
      customLabels: config.customLabels,
      labelPrefix: config.labelPrefix,
      // A frontend deployed before the backend sends this field gets no
      // `autoLabel` in the response; default to on so the capability doesn't
      // read as off while labeling is actually still pushing.
      autoLabel: config.autoLabel ?? true,
    };
    stateRef.current = seeded;
    for (const l of seeded.customLabels) seenCustomLabelIds.current.add(l.id);
    setBuiltIns(seeded.builtIns);
    setCustomLabels(seeded.customLabels);
    setLabelPrefix(seeded.labelPrefix);
    setAutoLabel(seeded.autoLabel);
  }, [config]);

  // Apply a partial change to local state; when `save`, persist the whole catalog.
  const apply = useCallback(
    (next: Partial<CatalogState>, save: boolean) => {
      const snap: CatalogState = { ...stateRef.current, ...next };
      stateRef.current = snap;
      setBuiltIns(snap.builtIns);
      setCustomLabels(snap.customLabels);
      setLabelPrefix(snap.labelPrefix);
      setAutoLabel(snap.autoLabel);
      if (save) {
        // Never send a half-typed (unnamed) draft custom label: the server rejects
        // name="" (min 1), which would fail the whole save and silently drop the
        // change that triggered it. Persist only named labels; an unnamed draft
        // stays local until it gets a name, then its own commit saves it.
        const named = snap.customLabels.filter((l) => l.name.trim() !== "");
        for (const l of named) seenCustomLabelIds.current.add(l.id);
        saveLabelConfig(systemId, {
          ...snap,
          customLabels: named,
          seenCustomLabelIds: [...seenCustomLabelIds.current],
        }).catch(() => errorToast("Failed to save labels"));
      }
    },
    [systemId, saveLabelConfig],
  );

  if (isLoading || !config) {
    return <div className="text-sm text-muted-foreground">Loading labels…</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <ToggleRow
        label="Apply labels to your mailbox"
        description="When on, classified labels are applied in your Gmail or Outlook. When off, labels are still computed in Okthx but never pushed to your mailbox."
        checked={autoLabel}
        onChange={(v) => apply({ autoLabel: v }, true)}
        dimmed={paused}
      />

      <ToggleRow
        label="Label prefix"
        description={'Prefix labels with "Okthx" so they appear as "Okthx/LabelName".'}
        checked={labelPrefix}
        onChange={(v) => apply({ labelPrefix: v }, true)}
        dimmed={paused}
      />

      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Built-in labels
        </div>
        <div className="flex flex-col gap-2">
          {builtIns.map((label, i) => (
            <BuiltInRow
              key={label.id}
              label={label}
              paused={paused}
              onChange={(next) =>
                apply({ builtIns: updateAt(stateRef.current.builtIns, i, next) }, true)
              }
            />
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Your labels
          </div>
          <Button
            data-statsig-name="label-catalog-add-custom"
            variant="outline"
            size="xs"
            onClick={() =>
              apply(
                {
                  customLabels: [
                    ...stateRef.current.customLabels,
                    {
                      id: crypto.randomUUID(),
                      name: "",
                      description: "",
                      color: "gray",
                      group: "type",
                      autoArchive: false,
                      enabled: true,
                    },
                  ],
                },
                false,
              )
            }
          >
            Add label
          </Button>
        </div>
        <div className="flex flex-col gap-2">
          {customLabels.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No custom labels yet. Add one to extend the catalog.
            </div>
          ) : (
            customLabels.map((label, i) => (
              <CustomRow
                key={label.id}
                label={label}
                paused={paused}
                onChange={(next) =>
                  apply({ customLabels: updateAt(stateRef.current.customLabels, i, next) }, false)
                }
                onCommit={(next) =>
                  apply({ customLabels: updateAt(stateRef.current.customLabels, i, next) }, true)
                }
                onDelete={() =>
                  apply(
                    { customLabels: stateRef.current.customLabels.filter((_, idx) => idx !== i) },
                    true,
                  )
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
